import { useAccount } from 'wagmi';
import { Navigate } from 'react-router-dom';
import VideoCard from '../../components/VideoCard/VideoCard';
import { useAllVideos, useVideo, useVideoBets, useVideoTotalPot } from '../../hooks/useContracts';
import { formatEther } from 'viem';
import DAppHeader from '../../components/DAppHeader/DAppHeader';
import { formatNumber } from '../../utils/rangeParser';

function VideoCardWrapper({ videoId, index, isBettingClosed = false }: { videoId: bigint; index: number; isBettingClosed?: boolean }) {
  const { video, isLoading: isLoadingVideo } = useVideo(videoId);
  const { betIds, isLoading: isLoadingBets } = useVideoBets(videoId);
  const { totalPot, isLoading: isLoadingPot } = useVideoTotalPot(betIds);

  if (isLoadingVideo || isLoadingBets || isLoadingPot || !video) {
    return (
      <div className="video-card-loading">
        <p>Loading video...</p>
      </div>
    );
  }

  // Calculate time remaining until betting deadline
  const now = Math.floor(Date.now() / 1000);
  const bettingDeadline = Number(video.bettingDeadline);
  const timeRemaining = bettingDeadline - now;

  const hours = Math.floor(timeRemaining / 3600);
  const minutes = Math.floor((timeRemaining % 3600) / 60);
  const closesIn = timeRemaining > 0 ? `${hours}h ${minutes}m` : 'Closed';

  // Extract username from URL (simplified - assumes Twitter URL format)
  const username ='@unknown';

  // Determine color based on index
  const colors = ['purple', 'cyan', 'yellow'] as const;
  const color = colors[index % colors.length];

  // Format total pot
  const totalPotFormatted = isLoadingPot
    ? 'Loading...'
    : totalPot > 0n
      ? `${formatEther(totalPot)} ETH`
      : '0 ETH';

  // Generate prediction options from contract thresholds
  const { threshold1, threshold2, threshold3 } = video.thresholds;
  const predictionOptions = [
    { range: `0-${formatNumber(threshold1)}`, multiplier: '1.8x', pot: '0 ETH' },
    { range: `${formatNumber(threshold1)}-${formatNumber(threshold2)}`, multiplier: '2.3x', pot: '0 ETH' },
    { range: `${formatNumber(threshold2)}-${formatNumber(threshold3)}`, multiplier: '3.7x', pot: '0 ETH' },
    { range: `${formatNumber(threshold3)}+`, multiplier: '6.2x', pot: '0 ETH' },
  ];

  const videoData = {
    id: Number(videoId),
    username,
    category: video.platform,
    duration: '0:00', // Not available from contract
    currentViews: Number(video.views),
    question: 'How many views will this video reach?',
    predictionOptions,
    totalPot: totalPotFormatted,
    totalPredictions: betIds?.length || 0,
    closesIn,
    color,
    videoUrl: video.videoUrl,
    postUrl: video.postUrl,
    bettingDeadline: video.bettingDeadline, // Pass bettingDeadline
  };

  return <VideoCard key={Number(videoId)} video={videoData} videoId={videoId} isBettingClosed={isBettingClosed} />;

}

export default function DAppPage() {
  const { isConnected } = useAccount();
  const { activeVideos, isLoading, error } = useAllVideos();

  // Redirect to landing page if not connected
  if (!isConnected) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="dapp-page">
      <DAppHeader />

      <main className="dapp-main">
        <div className="container">
          <div className="dapp-content">
            {/* Active Predictions Section */}
            <section className="dapp-section">
              <h1 className="dapp-title">Active Predictions</h1>
              <p className="dapp-subtitle">
                Place your predictions on viral videos and win rewards
              </p>

              {isLoading && (
                <div className="loading-state">
                  <p>Loading videos...</p>
                </div>
              )}

              {error && (
                <div className="error-state">
                  <p>Error loading videos: {error.message}</p>
                </div>
              )}

              {!isLoading && !error && activeVideos && activeVideos.length === 0 && (
                <div className="empty-state">
                  <p>No active videos available for betting at the moment.</p>
                </div>
              )}

              {!isLoading && !error && activeVideos && activeVideos.length > 0 && (
                <div className="video-grid">
                  {activeVideos.map((video, index) => (
                    <VideoCardWrapper key={Number(video.id)} videoId={video.id} index={index} />
                  ))}
                </div>
              )}
            </section>
          </div>
        </div>
      </main>
    </div>
  );
}
